import { useSidebarState } from '@/hooks/use-sidebar.hook';
import { createContext, useContext, useMemo } from 'react';
import type { SidebarContextValue } from './types';

// ── State context — re-renders consumers when isCollapsed/isMobileOpen change
type SidebarState = Pick<SidebarContextValue, 'isCollapsed' | 'isMobileOpen'>;
const SidebarStateContext = createContext<SidebarState>({
  isCollapsed: false,
  isMobileOpen: false,
});

// ── Actions context — stable references, never triggers re-renders
type SidebarActions = Pick<
  SidebarContextValue,
  'toggle' | 'expand' | 'collapse' | 'openMobile' | 'closeMobile'
>;
const noop = () => {};
const SidebarActionsContext = createContext<SidebarActions>({
  toggle: noop,
  expand: noop,
  collapse: noop,
  openMobile: noop,
  closeMobile: noop,
});

/**
 * Full sidebar context — subscribes to BOTH state + actions.
 * Use this when you need `isCollapsed` or `isMobileOpen`.
 */
export function useSidebar(): SidebarContextValue {
  const state = useContext(SidebarStateContext);
  const actions = useContext(SidebarActionsContext);
  return useMemo(() => ({ ...state, ...actions }), [state, actions]);
}

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const sidebarState = useSidebarState();

  const state: SidebarState = useMemo(
    () => ({
      isCollapsed: sidebarState.isCollapsed,
      isMobileOpen: sidebarState.isMobileOpen,
    }),
    [sidebarState.isCollapsed, sidebarState.isMobileOpen]
  );

  const actions: SidebarActions = useMemo(
    () => ({
      toggle: sidebarState.toggle,
      expand: sidebarState.expand,
      collapse: sidebarState.collapse,
      openMobile: sidebarState.openMobile,
      closeMobile: sidebarState.closeMobile,
    }),
    [
      sidebarState.toggle,
      sidebarState.expand,
      sidebarState.collapse,
      sidebarState.openMobile,
      sidebarState.closeMobile,
    ]
  );

  return (
    <SidebarActionsContext.Provider value={actions}>
      <SidebarStateContext.Provider value={state}>
        {children}
      </SidebarStateContext.Provider>
    </SidebarActionsContext.Provider>
  );
}
